import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { DEAL_STATUS_COLORS } from '@/types/deal';
import type { Deal } from '@/types/deal';
import { useModalStore } from '@/stores';
import { QuickCreateDealModal } from '@/components/Deals/QuickCreateDealModal';
import styles from './DealCalendarView.module.css';

interface DealCalendarViewProps {
  deals: Deal[];
}

export const DealCalendarView = ({ deals }: DealCalendarViewProps) => {
  const navigate = useNavigate();
  const { openModal } = useModalStore();
  const [currentDate, setCurrentDate] = useState(new Date());

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDayOfWeek = new Date(year, month, 1).getDay();

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  const dealsByDate = useMemo(() => {
    const map: Record<string, Deal[]> = {};
    deals.forEach((deal) => {
      if (deal.event_date) {
        const key = deal.event_date;
        if (!map[key]) map[key] = [];
        map[key].push(deal);
      }
    });
    return map;
  }, [deals]);

  const unscheduled = deals.filter((d) => !d.event_date);

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));

  const cells = [];
  for (let i = 0; i < firstDayOfWeek; i++) {
    cells.push(<div key={`empty-${i}`} className={styles.emptyCell} />);
  }
  for (let day = 1; day <= daysInMonth; day++) {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const dayDeals = dealsByDate[dateStr] || [];
    const isToday = dateStr === new Date().toISOString().split('T')[0];

    const isEmpty = dayDeals.length === 0;

    cells.push(
      <div
        key={day}
        className={`${styles.dayCell} ${isToday ? styles.today : ''} ${isEmpty ? styles.dayCellEmpty : ''}`}
        onClick={isEmpty ? () => openModal(<QuickCreateDealModal initialDate={dateStr} />) : undefined}
      >
        <div className={styles.dayHeader}>
          <span className={styles.dayNumber}>{day}</span>
          <button
            className={styles.addDealButton}
            onClick={(e) => {
              e.stopPropagation();
              openModal(<QuickCreateDealModal initialDate={dateStr} />);
            }}
            title="New deal"
          >
            <FontAwesomeIcon icon={faPlus} />
          </button>
        </div>
        <div className={styles.dayDeals}>
          {dayDeals.slice(0, 3).map((deal) => (
            <div
              key={deal.id}
              className={styles.calendarDeal}
              style={{ borderLeftColor: DEAL_STATUS_COLORS[deal.status] }}
              onClick={() => navigate(`/dashboard/deals/${deal.id}`)}
              title={deal.notes || deal.client_name}
            >
              <span className={styles.calendarDealTitle}>{deal.client_name}</span>
              {deal.notes && <span className={styles.calendarDealNotes}>{deal.notes}</span>}
            </div>
          ))}
          {dayDeals.length > 3 && (
            <span className={styles.moreDeals}>+{dayDeals.length - 3} more</span>
          )}
        </div>
      </div>,
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.calendarSection}>
        <div className={styles.calendarHeader}>
          <button className={styles.navButton} onClick={prevMonth}>
            ←
          </button>
          <h2 className={styles.monthTitle}>
            {monthNames[month]} {year}
          </h2>
          <button className={styles.navButton} onClick={nextMonth}>
            →
          </button>
        </div>
        <div className={styles.dayNames}>
          {dayNames.map((d) => (
            <div key={d} className={styles.dayName}>
              {d}
            </div>
          ))}
        </div>
        <div className={styles.grid}>{cells}</div>
      </div>
      {unscheduled.length > 0 && (
        <div className={styles.unscheduledSection}>
          <h3 className={styles.unscheduledTitle}>Unscheduled ({unscheduled.length})</h3>
          {unscheduled.map((deal) => (
            <div
              key={deal.id}
              className={styles.unscheduledDeal}
              onClick={() => navigate(`/dashboard/deals/${deal.id}`)}
            >
              <span
                className={styles.unscheduledDot}
                style={{ background: DEAL_STATUS_COLORS[deal.status] }}
              />
              <div className={styles.unscheduledContent}>
                <span className={styles.unscheduledName}>{deal.client_name}</span>
                {deal.notes && <span className={styles.unscheduledNotes}>{deal.notes}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
